import {
  BookOpen,
  ClipboardList,
  FlaskConical,
  Music,
  Palette,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { ActivityType, ClassActivity } from '@/types/activity';
import { ACTIVITY_TYPE_LABEL } from '../../activities/constants';

export type TabKey = 'all' | 'upcoming' | 'ongoing' | 'completed' | 'draft' | 'cancelled';

/** Where one activity stands. Every activity is in exactly one. */
export type ActivityState = Exclude<TabKey, 'all'>;

/**
 * Withdrawn, a draft, past its due date, due today, or due later.
 *
 * A draft outranks the date states: the class has not been given it, so it is
 * not upcoming, ongoing or completed for anybody yet.
 *
 * One rule for the tabs, the summary tiles and each row's label, so a count
 * and the list it opens can never disagree. Activities have no start date —
 * every one is live from the moment it is set — so "upcoming" and "ongoing"
 * are told apart by the due date alone: ongoing is the last day to hand in.
 * Past its date is completed whatever was handed in; marking late work does not
 * reopen it.
 */
export function activityState(activity: ClassActivity, today: string): ActivityState {
  if (activity.withdrawnAt !== null) return 'cancelled';
  if (activity.publication === 'DRAFT') return 'draft';
  if (activity.dueDate < today) return 'completed';
  if (activity.dueDate === today) return 'ongoing';
  return 'upcoming';
}

/** The pill each state wears in the list. */
export const ACTIVITY_STATE_PILL: Record<ActivityState, { label: string; className: string }> = {
  upcoming: { label: 'Upcoming', className: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  ongoing: { label: 'Ongoing', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  completed: {
    label: 'Completed',
    className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  },
  draft: { label: 'Draft', className: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  cancelled: { label: 'Cancelled', className: 'bg-rose-500/15 text-rose-400 border-rose-500/30' },
};

interface ActivityTypeTheme {
  icon: LucideIcon;
  bg: string;
  typeTone: string;
}

/** Artwork per kind of activity. The words come from `ACTIVITY_TYPE_LABEL`. */
const THEME_BY_TYPE: Record<ActivityType, ActivityTypeTheme> = {
  LAB_PRACTICAL: {
    icon: FlaskConical,
    bg: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25',
    typeTone: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25',
  },
  INDIVIDUAL_PROJECT: {
    icon: Palette,
    bg: 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
    typeTone: 'bg-amber-500/10 text-amber-400 border-amber-500/25',
  },
  GROUP_ACTIVITY: {
    icon: Users,
    bg: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/25',
    typeTone: 'bg-blue-500/10 text-blue-400 border-blue-500/25',
  },
  PRESENTATION: {
    icon: BookOpen,
    bg: 'bg-purple-500/15 text-purple-400 border border-purple-500/25',
    typeTone: 'bg-purple-500/10 text-purple-400 border-purple-500/25',
  },
  ART_CRAFT: {
    icon: Music,
    bg: 'bg-rose-500/15 text-rose-400 border border-rose-500/25',
    typeTone: 'bg-rose-500/10 text-rose-400 border-rose-500/25',
  },
  FIELD_RESEARCH: {
    icon: ClipboardList,
    bg: 'bg-violet-500/15 text-violet-400 border border-violet-500/25',
    typeTone: 'bg-blue-500/10 text-blue-400 border-blue-500/25',
  },
};

const UNKNOWN_TYPE_THEME: ActivityTypeTheme = {
  icon: ClipboardList,
  bg: 'bg-surface-2 text-ink-3 border border-line',
  typeTone: 'bg-surface-2 text-ink-2 border-line',
};

/**
 * The icon, colours and label one activity type wears in the list.
 *
 * The label is the console's own wording: this map used to keep its own, in
 * which a presentation read "In Class" and a type it did not know borrowed
 * another kind's name by row position. A type newer than this build shows as
 * the API sent it rather than as something it is not.
 */
export function getActivityTheme(activityType: ActivityType) {
  const theme = THEME_BY_TYPE[activityType] ?? UNKNOWN_TYPE_THEME;
  const typeLabel: string = ACTIVITY_TYPE_LABEL[activityType] ?? (activityType || '—');
  return { ...theme, typeLabel };
}

/** Returns day and uppercase month initials e.g. "08" and "SEP" */
export function parseDateParts(dateString: string) {
  const date = new Date(`${dateString}T00:00:00Z`);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
  return { day, month };
}
